import uicRequest from '@Utils/uicRequest';
import { UserCampaignPreparationUuid, UserInfoFromDL } from '@Types/dashboard';
import {
  GeneralTrialStatus,
  SolutionTrialEligibilityResponse,
} from '@Types/credit';
import { AxiosPromise } from 'axios';

/**
 * UIC Permissions Response
 */
export interface UicPermissionsResponse {
  permissions?: {
    visionOneTrials?: {
      manageTrials?: number;
    };
    [key: string]: any;
  };
  flagsState: {
    'credit-one-consumption-model': number;
    [key: string]: number;
  };
}

function createCasInstance() {
  return uicRequest({
    url: `/ui/uic/v3/instances/managedInstance`,
    method: 'post',
    data: {
      instanceType: 'sca',
    },
  });
}

function getCasInstances() {
  return uicRequest({
    url: `/ui/uic/v3/connectors/sca/orgs`,
    method: 'get',
  });
}

function getSolutionTrialEligibilityStatus(
  appCode = 'V1_EMAILSECURITY'
): Promise<SolutionTrialEligibilityResponse> {
  return uicRequest({
    url: `/ui/bc/api/trial/solution/eligibility/${appCode}`,
    method: 'get',
  });
}

/**
 * Get Credit Info by Company ID
 * Used to check if customer is in general trial (stockType === "Complimentary")
 *
 * @param companyId - Company ID
 * @returns Promise with credit information
 */
function getGeneralTrialStatus(): Promise<GeneralTrialStatus> {
  return uicRequest({
    url: `/ui/bc/api/trial/general/status`,
    method: 'get',
  });
}

function getUicPermissions(): Promise<UicPermissionsResponse> {
  return uicRequest({
    method: 'get',
    url: `/ui/uic/v3/permissions`,
  });
}

/**
 * Skip Solution Trial API
 * Used when customer is in trial stage and wants to use paid credit for provisioning
 * This will stop the trial and transfer all configured solutions to paid mode
 *
 * @param appCode - Application code (default: 'V1_EMAILSECURITY')
 * @param applicant - Applicant identifier (default: 'product instance')
 * @returns Promise
 */
function skipSolutionTrial(appCode = 'V1_EMAILSECURITY'): AxiosPromise<void> {
  return uicRequest({
    url: `/ui/bc/api/trial/solution/skiptopaid`,
    method: 'post',
    data: {
      appCode,
    },
  });
}

/**
 * Set Feature Status by Feature ID
 * Used to extend paid credit for features during lock period
 * This API enables or disables a specific feature
 *
 * @param featureId - Feature ID (e.g., 'ECS' for Email and Collaboration Security)
 * @param isEnabled - true to enable feature, false to disable
 * @returns Promise with feature status response
 */
function setFeatureStatus(
  featureId: string,
  isEnabled: boolean
): AxiosPromise<{
  featureId: string;
  businessId: string;
  isEnabled: boolean;
  meterIds: string[];
}> {
  return uicRequest({
    url: `/ui/bc/api/features/${featureId}/status`,
    method: 'put',
    data: {
      isEnabled,
    },
  });
}

function getUserInfoFromDL(email: string): AxiosPromise<UserInfoFromDL> {
  return uicRequest({
    url: `/internal/v2/direct/dlt/trilogy/api/v1/riskOverview/userAccounts/userIDByUPN?upn=${email}`,
    method: 'get',
  });
}

function getUserCampaignPreparationUuid(
  assetId: string
): AxiosPromise<UserCampaignPreparationUuid> {
  return uicRequest({
    url: `/internal/v2/direct/dlt/trilogy/api/v1/saa/training-campaign`,
    method: 'post',
    data: {
      productCode: 'sca',
      assetId,
    },
  });
}

function createCampaignPreparation(
  emails: string[]
): AxiosPromise<{ campaign_preparation_uuid: string }> {
  return uicRequest({
    url: `/public/saa/api/trs/campaign-preparations`,
    method: 'post',
    data: {
      emails,
      product_code: 'sca',
    },
  });
}

export default {
  createCasInstance,
  getCasInstances,
  getSolutionTrialEligibilityStatus,
  skipSolutionTrial,
  setFeatureStatus,
  getUserInfoFromDL,
  getUserCampaignPreparationUuid,
  createCampaignPreparation,
  getGeneralTrialStatus,
  getUicPermissions,
};
